import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import {
  X,
  FileText,
  Image as ImageIcon,
  AlertCircle,
  Code,
  AlertTriangle,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  ChevronDown,
  ChevronsDown,
  ChevronsUp,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { loader } from '@monaco-editor/react';
import { t } from '../../i18n';

// Import Monaco setup (must be before Editor)
import { monaco } from '../../../../monacoSetup';

// Configure loader to use local monaco
loader.config({ monaco });

// Lazy import Editor to ensure monaco is configured first
const Editor = React.lazy(() => import('@monaco-editor/react').then(mod => ({ default: mod.Editor })));

const SUPPORTED_ENCODINGS = [
  'UTF-8', 'GBK', 'GB18030', 'Big5', 'Shift_JIS', 'EUC-KR',
  'Windows-1252', 'ISO-8859-1', 'UTF-16LE', 'UTF-16BE',
];

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico'];

// Map file extensions to Monaco language IDs
const EXTENSION_TO_LANGUAGE: Record<string, string> = {
  'js': 'javascript', 'jsx': 'javascript', 'mjs': 'javascript',
  'ts': 'typescript', 'tsx': 'typescript',
  'json': 'json', 'jsonc': 'json',
  'html': 'html', 'htm': 'html',
  'css': 'css', 'scss': 'scss', 'less': 'less',
  'xml': 'xml', 'svg': 'xml',
  'md': 'markdown', 'markdown': 'markdown',
  'yaml': 'yaml', 'yml': 'yaml',
  'py': 'python',
  'go': 'go',
  'java': 'java',
  'c': 'c', 'h': 'c',
  'cpp': 'cpp', 'hpp': 'cpp', 'cc': 'cpp',
  'cs': 'csharp',
  'php': 'php',
  'rb': 'ruby',
  'rs': 'rust',
  'sql': 'sql',
  'sh': 'shell', 'bash': 'shell',
  'bat': 'bat', 'cmd': 'bat',
  'ps1': 'powershell',
  'ini': 'ini', 'conf': 'ini',
  'dockerfile': 'dockerfile',
};

const READ_CHUNK_SIZE = 255 * 1024;
const TEXT_PREVIEW_LIMIT = 1024 * 1024;
const TEXT_APPEND_SIZE = 512 * 1024;
const HEX_PREVIEW_LIMIT = 1024 * 1024;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

const ENCODING_LABELS: Record<string, string> = {
  'UTF-8': 'utf-8',
  'GBK': 'gbk',
  'GB18030': 'gb18030',
  'Big5': 'big5',
  'Shift_JIS': 'shift_jis',
  'EUC-KR': 'euc-kr',
  'Windows-1252': 'windows-1252',
  'ISO-8859-1': 'iso-8859-1',
  'UTF-16LE': 'utf-16le',
  'UTF-16BE': 'utf-16be',
};

const getLanguage = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return EXTENSION_TO_LANGUAGE[ext] || 'plaintext';
};

const getMimeType = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'bmp':
      return 'image/bmp';
    case 'svg':
      return 'image/svg+xml';
    case 'ico':
      return 'image/x-icon';
    default:
      return 'application/octet-stream';
  }
};

const base64ToBytes = (base64: string): Uint8Array => {
  if (!base64) return new Uint8Array();
  const binaryStr = atob(base64);
  const len = binaryStr.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  return bytes;
};

const concatBytes = (chunks: Uint8Array[]): Uint8Array => {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const merged = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }
  return merged;
};

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unitIndex = -1;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const precision = value >= 10 || unitIndex === 0 ? 1 : 2;
  return `${value.toFixed(precision)} ${units[unitIndex]}`;
};

const buildHexView = (bytes: Uint8Array, totalBytes: number): string => {
  const viewBytes = bytes.slice(0, Math.min(bytes.length, HEX_PREVIEW_LIMIT));
  let hexOutput = '';
  const stride = 16;
  for (let i = 0; i < viewBytes.length; i += stride) {
    const chunk = viewBytes.subarray(i, i + stride);
    hexOutput += i.toString(16).padStart(8, '0') + '  ';

    let hexPart = '';
    for (let j = 0; j < stride; j++) {
      if (j < chunk.length) {
        hexPart += chunk[j].toString(16).padStart(2, '0') + ' ';
      } else {
        hexPart += '   ';
      }
      if (j === 7) hexPart += ' ';
    }
    hexOutput += hexPart + ' |';

    let asciiPart = '';
    for (let j = 0; j < chunk.length; j++) {
      const byte = chunk[j];
      asciiPart += (byte >= 32 && byte <= 126) ? String.fromCharCode(byte) : '.';
    }
    hexOutput += asciiPart + '|\n';
  }

  if (totalBytes > viewBytes.length) {
    hexOutput += t('previewHexTruncated', {
      shown: formatBytes(viewBytes.length),
      total: formatBytes(totalBytes)
    });
  }
  return hexOutput;
};

const PreviewModal: React.FC = () => {
  const { previewFile, closePreview, fs } = useFileManagerStore();

  // Data state
  const [fileBytes, setFileBytes] = useState<Uint8Array | null>(null);
  const fileBytesRef = useRef<Uint8Array | null>(null);
  const [textContent, setTextContent] = useState('');
  const [imageObjectUrl, setImageObjectUrl] = useState<string | null>(null);
  const [imageMeta, setImageMeta] = useState<{ width: number; height: number } | null>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [encoding, setEncoding] = useState('UTF-8');
  const [showHex, setShowHex] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [textOffset, setTextOffset] = useState(0);
  const [textLoadedBytes, setTextLoadedBytes] = useState(0);
  const [textMode, setTextMode] = useState<'head' | 'tail'>('head');
  const [decodeWarning, setDecodeWarning] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [fitToScreen, setFitToScreen] = useState(true);

  const loadTokenRef = useRef(0);

  const isImage = useMemo(() => {
    if (!previewFile) return false;
    const ext = previewFile.name.split('.').pop()?.toLowerCase() || '';
    return IMAGE_EXTENSIONS.includes(ext);
  }, [previewFile]);

  const hexContent = useMemo(() => {
    if (!showHex || !fileBytes || !previewFile) return '';
    return buildHexView(fileBytes, previewFile.size);
  }, [showHex, fileBytes, previewFile]);

  const decodeBytes = (bytes: Uint8Array, enc: string): string => {
    const label = ENCODING_LABELS[enc] || 'utf-8';
    try {
      setDecodeWarning(null);
      return new TextDecoder(label).decode(bytes);
    } catch {
      setDecodeWarning(t('previewEncodingUnsupported', { encoding: enc }));
      return new TextDecoder('utf-8').decode(bytes);
    }
  };

  useEffect(() => {
    fileBytesRef.current = fileBytes;
  }, [fileBytes]);

  useEffect(() => {
    if (!previewFile || isImage || showHex) return;
    const bytes = fileBytesRef.current;
    if (!bytes || bytes.length === 0) return;
    setTextContent(decodeBytes(bytes, encoding));
  }, [encoding, previewFile, isImage, showHex]);

  // Cleanup Object URL
  useEffect(() => {
    return () => {
      if (imageObjectUrl) {
        URL.revokeObjectURL(imageObjectUrl);
      }
    };
  }, [imageObjectUrl]);

  // Load File Data on change
  useEffect(() => {
    if (!previewFile) return;
    loadTokenRef.current += 1;
    const token = loadTokenRef.current;

    setLoading(true);
    setLoadingMore(false);
    setProgress(null);
    setError(null);
    setImageError(false);
    setIsTruncated(false);
    setTextOffset(0);
    setTextLoadedBytes(0);
    setTextMode('head');
    setDecodeWarning(null);
    setFileBytes(null);
    setTextContent('');
    setImageMeta(null);
    setZoom(1);
    setFitToScreen(true);
    setImageObjectUrl(null);

    const readChunk = async (offset: number, length: number) => {
      const result = await fs.readFile(previewFile.path, { offset, length });
      const bytes = base64ToBytes(result.data);
      const bytesRead = result.bytesRead || bytes.length;
      const chunkBytes = bytesRead < bytes.length ? bytes.slice(0, bytesRead) : bytes;
      return { chunkBytes, bytesRead, isEOF: result.isEOF };
    };

    const loadInitialTextHead = async () => {
      try {
        const length = Math.min(previewFile.size, TEXT_PREVIEW_LIMIT);
        const { chunkBytes, bytesRead, isEOF } = await readChunk(0, length);
        if (token !== loadTokenRef.current) return;
        setFileBytes(chunkBytes);
        setTextContent(decodeBytes(chunkBytes, encoding));
        setTextOffset(0);
        setTextLoadedBytes(bytesRead);
        setTextMode('head');
        setIsTruncated(bytesRead < previewFile.size || !isEOF);
      } catch (err) {
        if (token !== loadTokenRef.current) return;
        const message = err instanceof Error ? err.message : '';
        setError(message ? t('previewFailedFile', { message }) : t('previewFailedFileShort'));
      } finally {
        if (token === loadTokenRef.current) setLoading(false);
      }
    };

    const loadInitialImage = async () => {
      try {
        if (previewFile.size <= 0) {
          setLoading(false);
          return;
        }
        setProgress({ loaded: 0, total: previewFile.size });
        const parts: Uint8Array[] = [];
        const hexParts: Uint8Array[] = [];
        let hexBytes = 0;
        let offset = 0;

        while (offset < previewFile.size) {
          const length = Math.min(READ_CHUNK_SIZE, previewFile.size - offset);
          const { chunkBytes, bytesRead, isEOF } = await readChunk(offset, length);
          if (token !== loadTokenRef.current) return;
          if (chunkBytes.length === 0 || bytesRead === 0) break;

          parts.push(chunkBytes);
          if (hexBytes < HEX_PREVIEW_LIMIT) {
            const sliceSize = Math.min(chunkBytes.length, HEX_PREVIEW_LIMIT - hexBytes);
            if (sliceSize > 0) {
              hexParts.push(chunkBytes.slice(0, sliceSize));
              hexBytes += sliceSize;
            }
          }

          offset += bytesRead;
          setProgress({ loaded: Math.min(offset, previewFile.size), total: previewFile.size });

          if (isEOF) break;
        }

        if (token !== loadTokenRef.current) return;
        const blob = new Blob(parts, { type: getMimeType(previewFile.name) });
        const url = URL.createObjectURL(blob);
        setImageObjectUrl(url);
        setFileBytes(hexParts.length ? concatBytes(hexParts) : null);
        setIsTruncated(offset < previewFile.size);
      } catch (err) {
        if (token !== loadTokenRef.current) return;
        const message = err instanceof Error ? err.message : '';
        setError(message ? t('previewFailedImage', { message }) : t('previewFailedImageShort'));
      } finally {
        if (token === loadTokenRef.current) {
          setLoading(false);
          setProgress(null);
        }
      }
    };

    if (isImage) {
      loadInitialImage();
    } else {
      loadInitialTextHead();
    }
  }, [previewFile, fs, isImage]);

  const loadTextTail = async () => {
    if (!previewFile || loading || loadingMore) return;
    const token = loadTokenRef.current;
    setLoading(true);
    setError(null);
    try {
      const length = Math.min(previewFile.size, TEXT_PREVIEW_LIMIT);
      const offset = Math.max(0, previewFile.size - length);
      const result = await fs.readFile(previewFile.path, { offset, length });
      const bytes = base64ToBytes(result.data);
      const bytesRead = result.bytesRead || bytes.length;
      const chunkBytes = bytesRead < bytes.length ? bytes.slice(0, bytesRead) : bytes;

      if (token !== loadTokenRef.current) return;
      setFileBytes(chunkBytes);
      setTextContent(decodeBytes(chunkBytes, encoding));
      setTextOffset(offset);
      setTextLoadedBytes(bytesRead);
      setTextMode('tail');
      setIsTruncated(offset > 0 || !result.isEOF);
    } catch (err) {
      if (token !== loadTokenRef.current) return;
      const message = err instanceof Error ? err.message : '';
      setError(message ? t('previewFailedFile', { message }) : t('previewFailedFileShort'));
    } finally {
      if (token === loadTokenRef.current) setLoading(false);
    }
  };

  const loadTextHead = async () => {
    if (!previewFile || loading || loadingMore) return;
    const token = loadTokenRef.current;
    setLoading(true);
    setError(null);
    try {
      const length = Math.min(previewFile.size, TEXT_PREVIEW_LIMIT);
      const result = await fs.readFile(previewFile.path, { offset: 0, length });
      const bytes = base64ToBytes(result.data);
      const bytesRead = result.bytesRead || bytes.length;
      const chunkBytes = bytesRead < bytes.length ? bytes.slice(0, bytesRead) : bytes;

      if (token !== loadTokenRef.current) return;
      setFileBytes(chunkBytes);
      setTextContent(decodeBytes(chunkBytes, encoding));
      setTextOffset(0);
      setTextLoadedBytes(bytesRead);
      setTextMode('head');
      setIsTruncated(bytesRead < previewFile.size || !result.isEOF);
    } catch (err) {
      if (token !== loadTokenRef.current) return;
      const message = err instanceof Error ? err.message : '';
      setError(message ? t('previewFailedFile', { message }) : t('previewFailedFileShort'));
    } finally {
      if (token === loadTokenRef.current) setLoading(false);
    }
  };

  const loadMoreText = async () => {
    if (!previewFile || loadingMore || loading || textMode !== 'head') return;
    const token = loadTokenRef.current;
    const nextOffset = textOffset + textLoadedBytes;
    if (nextOffset >= previewFile.size) return;

    setLoadingMore(true);
    setError(null);
    try {
      const length = Math.min(TEXT_APPEND_SIZE, previewFile.size - nextOffset);
      const result = await fs.readFile(previewFile.path, { offset: nextOffset, length });
      const bytes = base64ToBytes(result.data);
      const bytesRead = result.bytesRead || bytes.length;
      const chunkBytes = bytesRead < bytes.length ? bytes.slice(0, bytesRead) : bytes;

      if (token !== loadTokenRef.current) return;
      setFileBytes(prev => {
        if (!prev || prev.length === 0) return chunkBytes;
        return concatBytes([prev, chunkBytes]);
      });
      setTextContent(prev => prev + decodeBytes(chunkBytes, encoding));
      setTextLoadedBytes(prev => prev + bytesRead);
      setIsTruncated(nextOffset + bytesRead < previewFile.size || !result.isEOF);
    } catch (err) {
      if (token !== loadTokenRef.current) return;
      const message = err instanceof Error ? err.message : '';
      setError(message ? t('previewFailedMore', { message }) : t('previewFailedMoreShort'));
    } finally {
      if (token === loadTokenRef.current) setLoadingMore(false);
    }
  };

  const handleZoomIn = () => {
    if (fitToScreen) return;
    setZoom(prev => Math.min(ZOOM_MAX, parseFloat((prev + ZOOM_STEP).toFixed(2))));
  };

  const handleZoomOut = () => {
    if (fitToScreen) return;
    setZoom(prev => Math.max(ZOOM_MIN, parseFloat((prev - ZOOM_STEP).toFixed(2))));
  };

  const handleFitToScreen = () => {
    setFitToScreen(true);
  };

  const handleActualSize = () => {
    setFitToScreen(false);
    setZoom(1);
  };

  if (!previewFile) return null;

  const canLoadMore = !isImage && !showHex && textMode === 'head' && textOffset + textLoadedBytes < previewFile.size;
  const showLoadTail = !isImage && !showHex && previewFile.size > TEXT_PREVIEW_LIMIT;
  const showLoadHead = !isImage && !showHex && textMode === 'tail';
  const nextAppendSize = Math.min(TEXT_APPEND_SIZE, Math.max(0, previewFile.size - (textOffset + textLoadedBytes)));

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div className="bg-[#1e1e1e] w-[80vw] h-[80vh] rounded-xl shadow-2xl border border-[#3e3e42] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#252526] border-b border-[#3e3e42]">
          <div className="flex items-center space-x-3 overflow-hidden">
            {isImage ? <ImageIcon size={20} className="text-purple-400" /> : <FileText size={20} className="text-blue-400" />}
            <span className="font-medium text-slate-200 truncate">{previewFile.name}</span>
            <span className="text-xs text-slate-500 hidden sm:block">({formatBytes(previewFile.size)})</span>
            {isImage && imageMeta && (
              <span className="text-xs text-slate-500 hidden md:block">
                {imageMeta.width} x {imageMeta.height}
              </span>
            )}
            {!isImage && textLoadedBytes > 0 && (
              <span className="text-xs text-slate-500 hidden md:block">
                {formatBytes(textLoadedBytes)} / {formatBytes(previewFile.size)}
              </span>
            )}
            {decodeWarning && !isImage && !showHex && (
              <span className="text-xs text-amber-500 hidden md:block" title={decodeWarning}>
                {t('previewEncodingFallback')}
              </span>
            )}
            {isTruncated && !isImage && (
              <span className="flex items-center text-amber-500 text-xs gap-1" title={t('previewPartialTip')}>
                <AlertTriangle size={14} />
                {t('previewPartial')}
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {!error && !loading && (
              <>
                <button
                  onClick={() => setShowHex(!showHex)}
                  className={`p-1.5 rounded transition-colors text-xs font-medium flex items-center gap-1 ${showHex ? 'bg-[#0e639c] text-white' : 'hover:bg-[#3c3c3c] text-[#ccc]'}`}
                  title={t('previewToggleHex')}
                >
                  <Code size={14} />
                  {t('previewHex')}
                </button>
                {!showHex && !isImage && (
                  <select
                    value={encoding}
                    onChange={(e) => setEncoding(e.target.value)}
                    className="bg-[#3c3c3c] border border-[#3e3e42] text-xs text-[#ccc] rounded px-2 py-1 outline-none focus:border-[#007acc]"
                  >
                    {SUPPORTED_ENCODINGS.map(enc => <option key={enc} value={enc}>{enc}</option>)}
                  </select>
                )}
                {!showHex && !isImage && (
                  <div className="flex items-center space-x-1">
                    {showLoadHead && (
                      <button
                        onClick={loadTextHead}
                        disabled={loadingMore}
                        className="px-2 py-1 rounded text-xs text-[#ccc] hover:bg-[#3c3c3c] disabled:opacity-60 flex items-center gap-1"
                        title={t('previewLoadHead')}
                      >
                        <ChevronsUp size={14} />
                        {t('previewHead')}
                      </button>
                    )}
                    {showLoadTail && (
                      <button
                        onClick={loadTextTail}
                        disabled={loadingMore}
                        className="px-2 py-1 rounded text-xs text-[#ccc] hover:bg-[#3c3c3c] disabled:opacity-60 flex items-center gap-1"
                        title={t('previewLoadTail')}
                      >
                        <ChevronsDown size={14} />
                        {t('previewTail')}
                      </button>
                    )}
                    {canLoadMore && (
                      <button
                        onClick={loadMoreText}
                        disabled={loadingMore}
                        className="px-2 py-1 rounded text-xs text-[#ccc] hover:bg-[#3c3c3c] disabled:opacity-60 flex items-center gap-1"
                        title={t('previewLoadNext')}
                      >
                        <ChevronDown size={14} />
                        {loadingMore ? t('previewLoading') : t('previewMore', { size: formatBytes(nextAppendSize) })}
                      </button>
                    )}
                  </div>
                )}
                {isImage && !showHex && (
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={handleFitToScreen}
                      className={`p-1.5 rounded transition-colors ${fitToScreen ? 'bg-[#0e639c] text-white' : 'hover:bg-[#3c3c3c] text-[#ccc]'}`}
                      title={t('previewFitToScreen')}
                    >
                      <Maximize2 size={14} />
                    </button>
                    <button
                      onClick={handleActualSize}
                      className={`p-1.5 rounded transition-colors ${!fitToScreen ? 'bg-[#0e639c] text-white' : 'hover:bg-[#3c3c3c] text-[#ccc]'}`}
                      title={t('previewActualSize')}
                    >
                      <Minimize2 size={14} />
                    </button>
                    <button
                      onClick={handleZoomOut}
                      disabled={fitToScreen || zoom <= ZOOM_MIN}
                      className="p-1.5 rounded transition-colors hover:bg-[#3c3c3c] text-[#ccc] disabled:opacity-60"
                      title={t('previewZoomOut')}
                    >
                      <ZoomOut size={14} />
                    </button>
                    <span className="text-xs text-slate-400 w-12 text-center">
                      {fitToScreen ? t('previewFit') : `${Math.round(zoom * 100)}%`}
                    </span>
                    <button
                      onClick={handleZoomIn}
                      disabled={fitToScreen || zoom >= ZOOM_MAX}
                      className="p-1.5 rounded transition-colors hover:bg-[#3c3c3c] text-[#ccc] disabled:opacity-60"
                      title={t('previewZoomIn')}
                    >
                      <ZoomIn size={14} />
                    </button>
                  </div>
                )}
              </>
            )}
            <button onClick={closePreview} className="p-1 hover:bg-[#3c3c3c] rounded transition-colors">
              <X size={20} className="text-[#ccc]" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden bg-[#1e1e1e] relative text-slate-300 font-mono text-sm">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#1e1e1e]/80 z-10">
              <div className="w-8 h-8 border-2 border-[#007acc] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {progress && loading && isImage && !showHex && (
            <div className="absolute top-3 left-3 right-3 bg-[#252526]/85 border border-[#3e3e42] rounded px-3 py-2 text-xs text-[#ccc] z-20">
              <div className="flex items-center justify-between">
                <span>{t('previewLoadingImage')}</span>
                <span>{formatBytes(progress.loaded)} / {formatBytes(progress.total)}</span>
              </div>
              <div className="mt-2 h-1 bg-[#3c3c3c] rounded overflow-hidden">
                <div
                  className="h-full bg-[#0e639c] transition-all"
                  style={{ width: `${progress.total ? (progress.loaded / progress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {error ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
              <AlertCircle size={48} className="mb-4 opacity-50" />
              <p>{error}</p>
            </div>
          ) : (isImage && !showHex) ? (
            <div className="flex items-center justify-center h-full overflow-auto p-4 relative">
              {imageObjectUrl && !imageError ? (
                <div
                  className={fitToScreen ? 'w-full h-full flex items-center justify-center' : ''}
                  onDoubleClick={() => (fitToScreen ? handleActualSize() : handleFitToScreen())}
                >
                  <div
                    style={
                      fitToScreen
                        ? {}
                        : {
                            width: imageMeta ? imageMeta.width * zoom : 'auto',
                            height: imageMeta ? imageMeta.height * zoom : 'auto',
                          }
                    }
                  >
                    <img
                      src={imageObjectUrl}
                      alt={previewFile.name}
                      className={fitToScreen ? 'max-w-full max-h-full object-contain shadow-lg rounded' : 'block w-full h-full shadow-lg rounded'}
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                      onLoad={(e) => setImageMeta({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
                      onError={() => setImageError(true)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center text-slate-500">
                  <ImageIcon size={48} className="mb-4 opacity-50 text-red-400" />
                  <p>{t('previewFailedImagePreview')}</p>
                  <p className="text-xs mt-2 text-slate-600">{t('previewTryHex')}</p>
                </div>
              )}
            </div>
          ) : (
            <React.Suspense fallback={
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-2 border-[#007acc] border-t-transparent rounded-full animate-spin" />
              </div>
            }>
              <Editor
                height="100%"
                language={showHex ? 'plaintext' : getLanguage(previewFile.name)}
                theme="vs-dark"
                value={showHex ? hexContent : textContent}
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  automaticLayout: true,
                  scrollBeyondLastLine: false,
                  lineNumbers: 'on',
                  renderLineHighlight: 'line',
                  wordWrap: showHex ? 'off' : 'on',
                  fontFamily: showHex ? '"Courier New", Courier, monospace' : undefined,
                }}
              />
            </React.Suspense>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PreviewModal;
